import { useEffect, useMemo, useState } from 'react'
import {
  collection,
  limit,
  onSnapshot,
  orderBy,
  query,
  type Timestamp,
} from 'firebase/firestore'
import { firestoreDb } from '../services/firebase'
import { isNoSignInPreviewActive } from '../debug'
import type { NotificationRecord } from '../types/schema'
import { requestAppDataRefresh } from '../utils/appDataRefresh'

const HARD_CAP = 200

const timestampMillis = (value: unknown): number => {
  if (value && typeof value === 'object' && 'toMillis' in value) {
    try {
      return (value as { toMillis: () => number }).toMillis()
    } catch {
      return 0
    }
  }
  return 0
}

const normalize = (userId: string, id: string, data: Record<string, unknown>): NotificationRecord => ({
  id,
  recipientId: userId,
  actorId: typeof data.actorId === 'string' ? data.actorId : null,
  workspaceId: typeof data.workspaceId === 'string' ? data.workspaceId : null,
  workspaceAudience: typeof data.workspaceAudience === 'string'
    ? data.workspaceAudience as NotificationRecord['workspaceAudience']
    : null,
  eventType: typeof data.eventType === 'string' ? data.eventType as NotificationRecord['eventType'] : 'SYSTEM_NOTICE',
  entityType: typeof data.entityType === 'string' ? data.entityType as NotificationRecord['entityType'] : 'SYSTEM',
  entityId: typeof data.entityId === 'string' ? data.entityId : null,
  entityTitle: typeof data.entityTitle === 'string' ? data.entityTitle : null,
  priority: data.priority === 'HIGH' || data.priority === 'MEDIUM' ? data.priority : 'LOW',
  channels: Array.isArray(data.channels)
    ? data.channels.filter((value): value is NotificationRecord['channels'][number] =>
        value === 'IN_APP' || value === 'PUSH' || value === 'EMAIL',
      )
    : ['IN_APP'],
  title: typeof data.title === 'string' ? data.title : 'Notification',
  body: typeof data.body === 'string' ? data.body : '',
  deepLink: typeof data.deepLink === 'string' ? data.deepLink : null,
  deeplink: typeof data.deepLink === 'string' ? data.deepLink : null,
  batchKey: typeof data.batchKey === 'string' ? data.batchKey : null,
  dedupeKey: typeof data.dedupeKey === 'string' ? data.dedupeKey : null,
  tier: data.tier === 'HIGH' || data.tier === 'LOW' ? data.tier : undefined,
  category: typeof data.category === 'string' ? data.category as NotificationRecord['category'] : undefined,
  isDigest: data.isDigest === true,
  digestCount: typeof data.digestCount === 'number' ? data.digestCount : undefined,
  digestItems: Array.isArray(data.digestItems) ? data.digestItems as NotificationRecord['digestItems'] : undefined,
  readAt: data.readAt as Timestamp | null ?? null,
  dismissedAt: data.dismissedAt as Timestamp | null ?? null,
  clearedAt: data.clearedAt as Timestamp | null ?? null,
  createdAt: data.createdAt ?? null,
  expiresAt: data.expiresAt ?? null,
  data: data.data && typeof data.data === 'object' ? data.data as Record<string, unknown> : undefined,
})

const EMPTY_NOTIFICATIONS: NotificationRecord[] = []

export const useNotificationListener = (userId: string | null) => {
  const isPreview = isNoSignInPreviewActive()
  const [subscribedState, setSubscribedState] = useState<{
    userId: string
    notifications: NotificationRecord[]
  } | null>(null)

  useEffect(() => {
    if (!userId || isPreview) {
      return
    }

    const currentUserId = userId
    const ref = query(
      collection(firestoreDb, 'notifications', currentUserId, 'items'),
      orderBy('createdAt', 'desc'),
      limit(400),
    )

    let initialized = false
    const unsubscribe = onSnapshot(ref, (snapshot) => {
      if (initialized && snapshot.docChanges().some((change) => change.type === 'added')) {
        requestAppDataRefresh('notification')
      }
      const next = snapshot.docs
        .map((entry) => normalize(currentUserId, entry.id, entry.data() as Record<string, unknown>))
        .filter((notification) => !notification.clearedAt)
        .filter((notification) => !notification.dismissedAt)
        .sort((a, b) => timestampMillis(b.createdAt) - timestampMillis(a.createdAt))
        .slice(0, HARD_CAP)
      setSubscribedState({ userId: currentUserId, notifications: next })
      initialized = true
    }, (error) => {
      console.warn('[Notifications] listener failed', error)
    })

    return () => unsubscribe()
  }, [isPreview, userId])

  const notifications = useMemo(() => {
    if (!userId || isPreview) return EMPTY_NOTIFICATIONS
    if (subscribedState?.userId === userId) return subscribedState.notifications
    return EMPTY_NOTIFICATIONS
  }, [isPreview, subscribedState, userId])

  return notifications
}
